import {
  getOnboardingData,
  singleConnectedSoftware
} from "../../redux/loginSlice";
import { getProfile } from "../../redux/userManagementSlice";
import {
  ACTIVE_ONBOARDING_ROUTE,
  COMPLETE_ACCOUNT_SETUP,
  LASTCONNECTEDSOFTWARE,
  PARTNER_DASHBOARD
} from "../../route/paths";
import { COMPLETED, DISCONNECTED } from "../../utils/constants/appConstants";
import { isOnboardingUserStatus } from "../../utils/onboardingVisibility";

const PARTNER_ROLE_NAME = "ReferralPartner";

export default function useGetOnboardingData({
  dispatch,
  navigate,
  checkSoftwareConnected
}) {
  const handleGetOnboardingData = async (successMessage) => {
    const [onboardingResult, profileResult, lastConnectedSoftData] =
      await Promise.allSettled([
        dispatch(getOnboardingData()).unwrap(),
        dispatch(getProfile()).unwrap(),
        dispatch(singleConnectedSoftware()).unwrap()
      ]);

    const response =
      onboardingResult.status === "fulfilled" ? onboardingResult.value : null;
    const apiResponse =
      profileResult.status === "fulfilled" ? profileResult.value : null;
    const singleConnectedData =
      lastConnectedSoftData.status === "fulfilled"
        ? lastConnectedSoftData.value?.data?.data?.connectionStatus
        : null;

    const onboardingDetails = response?.data?.data ?? {};
    const onboardingStatus = onboardingDetails?.onboardingStatus;
    const onboardingIsConcent = onboardingDetails?.isCreditConsentAccepted;

    const profileData = apiResponse?.data?.data;
    const forceApplicationStatus =
      profileData?.systemSettings?.forceApplicationProcess;

    // Account status decides whether this customer sees onboarding at all.
    // Only Pending, Archived and Customer DD do; a decided account is followed
    // up by hand, so it goes to the dashboard however much of its onboarding
    // is outstanding. A profile that failed to load answers false, which sends
    // the customer to the dashboard rather than into a flow that may hold
    // nothing for them. See docs/decisions/0060.
    const seesOnboarding = isOnboardingUserStatus(profileData?.userStatusId);

    const isPartner =
      Array.isArray(profileData?.roles) &&
      profileData.roles.some((r) => r.name === PARTNER_ROLE_NAME);

    if (isPartner) {
      navigate(PARTNER_DASHBOARD, { state: { successMessage } });
      return;
    }

    // Forced re-application is the one flow the MultiStepForm wizard serves:
    // the account has finished onboarding, so /onboarding has no outstanding
    // step to show it.
    if (
      onboardingStatus === COMPLETED &&
      onboardingIsConcent === true &&
      forceApplicationStatus
    ) {
      navigate(COMPLETE_ACCOUNT_SETUP, {
        state: { successMessage }
      });
      return;
    }

    if (singleConnectedData === DISCONNECTED) {
      navigate(LASTCONNECTEDSOFTWARE, {
        state: { successMessage }
      });
      return;
    }

    // Unfinished onboarding routes through ACTIVE_ONBOARDING_ROUTE, the constant
    // that selects the customer onboarding destination.
    if (
      seesOnboarding &&
      onboardingStatus !== COMPLETED &&
      onboardingIsConcent === true
    ) {
      navigate(ACTIVE_ONBOARDING_ROUTE, {
        state: { successMessage }
      });
      return;
    }

    checkSoftwareConnected(successMessage);
  };

  return handleGetOnboardingData;
}
